import { ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../../services/api';
import { Lead, Panel, PanelHeader, PanelBody } from '../../ui/Panel';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import { ErrorNotice, InfoNotice, SkeletonRows } from '../../ui/States';
import { errorMessage, inputClass, money, toCents } from '../../../lib/programs';
import { BudgetLine, EventMoney, dateText, moneyKey } from '../../../lib/eventMoney';
import type { SimplePanelProps } from '../adapt';
import { em, headRow, num, td, th } from '../logistics';
import { stack, useMarkPresent } from '../dayOfTypes';

export function useEventMoney(eventId: string) {
  return useQuery<EventMoney>(moneyKey(eventId), async () => (await api.get(`/events/${eventId}/money`)).data.data);
}

export function useMoneyRefresh(eventId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries(moneyKey(eventId));
    queryClient.invalidateQueries(['event-registration', eventId]);
  };
}

export function CountsOnly({ data }: { data: EventMoney }) {
  const c = data.counts;
  return (
    <InfoNotice>
      Amounts are shown to admins. {c.vendors} vendor{c.vendors === 1 ? '' : 's'} ({c.vendorsComplete} paid in full),{' '}
      {c.deadlinesOverdue} overdue deadline{c.deadlinesOverdue === 1 ? '' : 's'}, {c.giftCardRequests} gift-card request
      {c.giftCardRequests === 1 ? '' : 's'}.
    </InfoNotice>
  );
}

/** The key BudgetPanel marks while it is on the page: the tab's opening and viewer note are said there, once. */
export const budgetLeadKey = (eventId: string) => `budget-lead:${eventId}`;

/** Opens York Treasury's printable request in a new tab. */
export function useOpenTreasuryRequest(eventId: string) {
  return useMutation(
    async () => (await api.get(`/events/${eventId}/money/treasury-request`)).data.data as { html: string },
    {
      onSuccess: (doc) => {
        const w = window.open('', '_blank');
        if (w) {
          w.document.write(doc.html);
          w.document.close();
        }
      },
    },
  );
}

export const dollarsIn = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? '' : (cents / 100).toFixed(2);

export function cents(value: string, field: string): number | null {
  const c = toCents(value);
  if (Number.isNaN(c)) throw new Error(`${field}: enter an amount like 1250.00`);
  return c;
}

/** A labelled field in a money dialog: label above, hint below. */
export function MoneyField({
  label,
  children,
  wide = false,
  hint,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
  hint?: ReactNode;
}) {
  return (
    <label className={`block ${wide ? 'md:col-span-2' : ''}`}>
      <span className="block text-[15px] leading-5 font-semibold text-ink mb-[6px]">{label}</span>
      {children}
      {hint ? <span className="block text-small text-ink-3 mt-1">{hint}</span> : null}
    </label>
  );
}

export const moneyError = (err: unknown) => (
  <div className="mb-4">
    <ErrorNotice>{errorMessage(err, (err as Error)?.message || 'Could not save.')}</ErrorNotice>
  </div>
);

function LineDialog({
  eventId,
  data,
  line,
  onDone,
}: {
  eventId: string;
  data: EventMoney;
  line?: BudgetLine;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    bucket: line?.bucket ?? 'food',
    label: line?.label ?? '',
    planned: dollarsIn(line?.plannedCents),
    committed: dollarsIn(line?.committedCents),
    actual: dollarsIn(line?.actualCents),
    note: line?.note ?? '',
  });
  const refresh = useMoneyRefresh(eventId);
  const save = useMutation(
    async () => {
      const body = {
        bucket: form.bucket,
        label: form.label,
        plannedCents: cents(form.planned, 'Budgeted'),
        committedCents: cents(form.committed, 'Committed'),
        actualCents: cents(form.actual, 'Actual'),
        note: form.note,
      };
      return line
        ? api.patch(`/events/money/lines/${line.id}`, body)
        : api.post(`/events/${eventId}/money/lines`, body);
    },
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  const archive = useMutation(async () => api.patch(`/events/money/lines/${line?.id}`, { archived: true }), {
    onSuccess: () => {
      refresh();
      onDone();
    },
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  const b = data.buckets!;
  return (
    <Modal
      title={line ? 'Edit the budget line' : 'Add a budget line'}
      why="For costs and revenue no vendor or ledger entry covers, such as parking, printing or the HST lines. Amounts look like 1250.00."
      onClose={onDone}
      width={620}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
            {line ? 'Save the line' : 'Add the line'}
          </Button>
          <Button onClick={onDone}>Cancel</Button>
          {line ? (
            <Button
              variant="destructive"
              className="ml-auto max-md:ml-0"
              disabled={archive.isLoading}
              onClick={() => archive.mutate()}
            >
              Remove from the budget
            </Button>
          ) : null}
        </>
      }
    >
      {save.error ? moneyError(save.error) : null}
      {archive.error ? moneyError(archive.error) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MoneyField label="Bucket" wide>
          <select className={inputClass} value={form.bucket} onChange={set('bucket')}>
            <optgroup label="Costs">
              {Object.entries(b.cost).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </optgroup>
            <optgroup label="Revenue">
              {Object.entries(b.revenue).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </optgroup>
          </select>
        </MoneyField>
        <MoneyField label="What it is" wide>
          <input
            className={inputClass}
            value={form.label}
            onChange={set('label')}
            placeholder="For example, parking codes from HONK"
          />
        </MoneyField>
        <MoneyField label="Budgeted">
          <input
            className={inputClass}
            value={form.planned}
            onChange={set('planned')}
            inputMode="decimal"
            placeholder="$"
          />
        </MoneyField>
        <MoneyField label="Committed">
          <input
            className={inputClass}
            value={form.committed}
            onChange={set('committed')}
            inputMode="decimal"
            placeholder="$"
          />
        </MoneyField>
        <MoneyField label="Actual">
          <input
            className={inputClass}
            value={form.actual}
            onChange={set('actual')}
            inputMode="decimal"
            placeholder="$"
          />
        </MoneyField>
        <MoneyField label="Note">
          <input className={inputClass} value={form.note} onChange={set('note')} />
        </MoneyField>
      </div>
    </Modal>
  );
}

function EReportsDialog({ eventId, data, onDone }: { eventId: string; data: EventMoney; onDone: () => void }) {
  const rows = data.budget!.rows;
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(rows.map((r) => [r.bucket, dollarsIn(r.eReportsCents)])),
  );
  const [asOf, setAsOf] = useState(data.finance?.eReportsAsOf ?? '');
  const [costCentre, setCostCentre] = useState(data.finance?.costCentre ?? '');
  const refresh = useMoneyRefresh(eventId);
  const save = useMutation(
    async () =>
      api.put(`/events/${eventId}/money/settings`, {
        costCentre,
        eReportsAsOf: asOf || null,
        eReports: Object.fromEntries(
          Object.entries(values).map(([k, v]) => [k, cents(v, rows.find((r) => r.bucket === k)?.label ?? k)]),
        ),
      }),
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  return (
    <Modal
      title="eReports and cost centre"
      why="Type what eReports shows for each line; the platform works out what is not in eReports yet."
      onClose={onDone}
      width={680}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
            Save
          </Button>
          <Button onClick={onDone}>Cancel</Button>
        </>
      }
    >
      {save.error ? moneyError(save.error) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MoneyField label="Cost centre">
          <input className={inputClass} value={costCentre} onChange={(e) => setCostCentre(e.target.value)} />
        </MoneyField>
        <MoneyField label="eReports as of">
          <input type="date" className={inputClass} value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </MoneyField>
        {rows.map((r) => (
          <MoneyField key={r.bucket} label={r.label}>
            <input
              className={inputClass}
              value={values[r.bucket] ?? ''}
              onChange={(e) => setValues({ ...values, [r.bucket]: e.target.value })}
              inputMode="decimal"
              placeholder="$"
            />
          </MoneyField>
        ))}
      </div>
      {!rows.length ? (
        <p className="text-small text-ink-3 mt-3">A line per bucket appears here once the budget has some.</p>
      ) : null}
    </Modal>
  );
}

type BudgetRowLike = Record<
  'plannedCents' | 'committedCents' | 'actualCents' | 'eReportsCents' | 'notInEReportsCents',
  number | null
>;

const SOURCE_TEXT = (s: { lines: number; vendors: number; ledger: number; eventbrite: number }) =>
  [
    s.vendors ? `${s.vendors} vendor${s.vendors === 1 ? '' : 's'}` : '',
    s.ledger ? `${s.ledger} ledger entr${s.ledger === 1 ? 'y' : 'ies'}` : '',
    s.lines ? `${s.lines} line${s.lines === 1 ? '' : 's'}` : '',
    s.eventbrite ? 'Eventbrite upload' : '',
  ]
    .filter(Boolean)
    .join(' · ');

const COLUMNS = [
  ['Budgeted', (r: BudgetRowLike) => r.plannedCents],
  ['Committed', (r: BudgetRowLike) => r.committedCents],
  ['Actual', (r: BudgetRowLike) => r.actualCents],
  ['In eReports', (r: BudgetRowLike) => r.eReportsCents],
  ['Not yet in eReports', (r: BudgetRowLike) => r.notInEReportsCents],
] as const;

const s = (n: number) => (n === 1 ? '' : 's');

/** The tab's opening sentence: where the money stands, and the Treasury request while it is still to send. */
function moneyLead(d: EventMoney): string {
  const parts: string[] = [];
  const r = d.finance?.eventbriteRequest;
  const salesSet = Boolean(r && (r.salesOpen || r.ticketTypes.length));
  if (d.visible && d.budget) {
    const t = d.budget.totals.cost;
    const open = (d.vendors ?? []).filter((v) => !v.complete && !v.cancelledAt).length;
    const unpaid = open ? `; ${open} vendor${s(open)} still ${open === 1 ? 'has' : 'have'} an unpaid invoice.` : '.';
    if (!t.plannedCents && !t.committedCents)
      parts.push(
        d.finance?.costCentre
          ? `Nothing is budgeted yet; the cost centre is ${d.finance.costCentre}${unpaid}`
          : `Nothing is budgeted yet and there’s no cost centre${unpaid}`,
      );
    else if (!t.plannedCents)
      parts.push(`Committed costs are ${money(t.committedCents)}; nothing is budgeted yet${unpaid}`);
    else parts.push(`Committed costs are ${money(t.committedCents)} of ${money(t.plannedCents)} budgeted${unpaid}`);
  } else {
    const c = d.counts;
    parts.push(
      `${c.vendors} vendor${s(c.vendors)} (${c.vendorsComplete} paid in full) and ${c.giftCardRequests} gift-card request${s(c.giftCardRequests)}.`,
    );
  }
  if (salesSet && !d.counts.treasuryRequestSent)
    parts.push('The Eventbrite request to York Treasury hasn’t been marked sent.');
  return parts.join(' ');
}

/** Budget per bucket: budgeted, committed, actual, in eReports and not yet in eReports. Opens the money tab. */
export default function BudgetPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useEventMoney(eventId);
  useMarkPresent(budgetLeadKey(eventId));
  const openRequest = useOpenTreasuryRequest(eventId);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<BudgetLine | null>(null);
  const [eReports, setEReports] = useState(false);
  if (q.isLoading) return <SkeletonRows rows={6} />;
  if (q.error || !q.data) return <ErrorNotice>{errorMessage(q.error, 'Could not load the budget.')}</ErrorNotice>;
  const d = q.data;
  const sub =
    'Costs and revenue for this event, the vendors who supply it, gift cards, and the Eventbrite paperwork with Treasury. Vendors and ledger entries fill the budget in as they’re added.';
  if (!d.visible)
    return (
      <>
        <Lead lead={moneyLead(d)} sub={sub} />
        <div className="mt-6">
          <CountsOnly data={d} />
        </div>
      </>
    );
  const b = d.budget!;
  const lines = (d.lines ?? []).filter((l) => !l.archivedAt);
  const empty = !b.rows.length && !lines.length;
  const r = d.finance?.eventbriteRequest;
  const treasuryFirst = canEdit && Boolean(r && (r.salesOpen || r.ticketTypes.length)) && !d.counts.treasuryRequestSent;
  const lineFirst = canEdit && !treasuryFirst && empty;
  // One set of columns for both tables, so Costs and Revenue line up; a column with nothing anywhere stays hidden.
  const cols = COLUMNS.filter(
    ([, get]) => b.rows.some((row) => get(row)) || get(b.totals.cost) || get(b.totals.revenue),
  );
  const table = (dir: 'cost' | 'revenue') => {
    const rows = b.rows.filter((row) => row.direction === dir);
    const t = b.totals[dir];
    if (!rows.length) return null;
    return (
      <div className="overflow-x-auto mb-8 last:mb-0">
        <table className="w-full min-w-[720px] table-fixed">
          <colgroup>
            <col className="w-[26%]" />
            {cols.map(([label]) => (
              <col key={label} />
            ))}
            <col className="w-[20%]" />
          </colgroup>
          <thead>
            <tr className={headRow}>
              <th className={th}>{dir === 'cost' ? 'Costs' : 'Revenue'}</th>
              {cols.map(([label]) => (
                <th key={label} className={`${th} ${num}`}>
                  {label}
                </th>
              ))}
              <th className={th}>From</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.bucket} className="border-b border-line hover:bg-selected">
                <td className={td}>
                  {row.label}
                  {row.missingAmounts ? (
                    <span className="block text-small text-ink-2">
                      {row.missingAmounts} ledger entr{row.missingAmounts === 1 ? 'y' : 'ies'} without an amount
                    </span>
                  ) : null}
                </td>
                {cols.map(([label, get]) => (
                  <td
                    key={label}
                    className={`${td} ${num} ${label === 'Not yet in eReports' && get(row) ? 'font-semibold' : ''}`}
                  >
                    {get(row) ? money(get(row)) : <span className="text-ink-3">–</span>}
                  </td>
                ))}
                <td className={`${td} text-small text-ink-2`}>{SOURCE_TEXT(row.sources)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-line-strong font-semibold">
              <td className={td}>Total</td>
              {cols.map(([label, get]) => (
                <td key={label} className={`${td} ${num}`}>
                  {get(t) ? money(get(t)) : <span className="text-ink-3 font-normal">–</span>}
                </td>
              ))}
              <td className={td} />
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };
  const bothActual = b.totals.cost.actualCents > 0 && b.totals.revenue.actualCents > 0;
  const bucketName = (k: string) => d.buckets!.cost[k] ?? d.buckets!.revenue[k] ?? k;
  return (
    <>
      <Lead lead={moneyLead(d)} sub={sub}>
        {treasuryFirst ? (
          <Button variant="primary" disabled={openRequest.isLoading} onClick={() => openRequest.mutate()}>
            Open the Treasury request
          </Button>
        ) : lineFirst ? (
          <Button variant="primary" onClick={() => setAdding(true)}>
            Add a line
          </Button>
        ) : null}
      </Lead>
      {openRequest.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(openRequest.error, 'Could not open the request.')}</ErrorNotice>
        </div>
      ) : null}
      <div className="h-8 max-md:h-4" />
      <Panel>
        <PanelHeader
          title="Budget"
          note={[
            d.finance?.costCentre ? `cost centre ${d.finance.costCentre}` : 'no cost centre yet',
            d.finance?.eReportsAsOf ? `eReports as of ${dateText(d.finance.eReportsAsOf)}` : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        >
          {canEdit ? <Button onClick={() => setEReports(true)}>eReports and cost centre</Button> : null}
          {canEdit && !lineFirst ? <Button onClick={() => setAdding(true)}>Add a line</Button> : null}
        </PanelHeader>
        <PanelBody>
          {empty ? (
            <div className="max-w-[72ch]">
              <p className="text-[15px] leading-[22px] text-ink">No lines yet.</p>
              <p className="text-[15px] leading-[22px] text-ink-2 mt-1">
                When there are some, two tables appear here, Costs and Revenue, with Budgeted, Committed, Actual and In
                eReports columns and a total. Vendors and the payments ledger fill them in by themselves: reimbursements
                count as travel, honoraria and gift cards as their own lines, and paid entries as actual. Add a line for
                anything else (parking, printing, the HST lines).
              </p>
            </div>
          ) : (
            <>
              {table('cost')}
              {table('revenue')}
              {bothActual ? (
                <p className="text-[15px] leading-[22px] text-ink mt-4">
                  Actual costs minus actual revenue:{' '}
                  <strong className="tabular">{money(b.totals.cost.actualCents - b.totals.revenue.actualCents)}</strong>
                </p>
              ) : null}
            </>
          )}
          {lines.length ? (
            <>
              <h3 className="font-serif font-semibold text-[17px] leading-6 text-ink mt-8 mb-2">
                Lines entered by hand
              </h3>
              <table className={stack.table}>
                <thead className={stack.thead}>
                  <tr className={headRow}>
                    <th className={th}>Line</th>
                    <th className={th}>Bucket</th>
                    <th className={`${th} ${num}`}>Committed</th>
                    <th className={`${th} ${num}`}>Actual</th>
                    <th className={`${th} w-[80px]`}>
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className={stack.tbody}>
                  {lines.map((l) => (
                    <tr key={l.id} className={stack.tr}>
                      <td className={`${td} ${stack.td} font-semibold`}>
                        {l.label}
                        {l.note ? <span className={`${em} font-normal`}>{l.note}</span> : null}
                      </td>
                      <td className={`${td} ${stack.td} ${stack.labelled}`} data-label="Bucket">
                        {bucketName(l.bucket)}
                      </td>
                      <td
                        className={`${td} ${stack.td} ${stack.labelled} md:text-right tabular`}
                        data-label="Committed"
                      >
                        {l.committedCents != null ? money(l.committedCents) : <span className="text-ink-3">–</span>}
                      </td>
                      <td className={`${td} ${stack.td} ${stack.labelled} md:text-right tabular`} data-label="Actual">
                        {l.actualCents != null ? money(l.actualCents) : <span className="text-ink-3">–</span>}
                      </td>
                      <td className={`${td} ${stack.td} ${stack.acts}`}>
                        {canEdit ? (
                          <Button
                            size="sm"
                            onClick={() => {
                              setEditing(l);
                              setAdding(false);
                            }}
                          >
                            Edit
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </PanelBody>
        {eReports ? <EReportsDialog eventId={eventId} data={d} onDone={() => setEReports(false)} /> : null}
        {adding ? <LineDialog eventId={eventId} data={d} onDone={() => setAdding(false)} /> : null}
        {editing ? (
          <LineDialog key={editing.id} eventId={eventId} data={d} line={editing} onDone={() => setEditing(null)} />
        ) : null}
      </Panel>
    </>
  );
}
